// Chinese (Simplified) curriculum translations
export const zh_curriculum = {
  1: {
    title: "数据集",
    subtitle: "字符级分词",
    heading: "第1关：数据集",
    body: `语言模型的燃料是文本数据。在生产环境的大语言模型中，每个文档可能是一个网页，但microgpt使用32,000个名字作为更简单的示例。

**字符级分词**为每个唯一字符分配一个整数。对于名字数据集，我们得到26个小写字母（a-z），给我们token ID 0-25。每个token只是一个离散符号——数字本身没有意义。

特殊的**BOS**（序列开始）token充当分隔符。每个名字都被包装：[BOS, e, m, m, a, BOS]。模型学习到BOS启动一个新名字，另一个BOS结束它。我们最终的词汇表：27个tokens（26个字母 + 1个BOS）。

**生产环境差异**：真实的分词器如tiktoken（GPT-4使用）使用BPE（字节对编码），约有100K个tokens，其中像"the"这样的常见词成为单个token。效率更高！`,
    question: "为什么我们在训练前使用`random.shuffle(docs)`？",
    answer: "为了防止模型学习依赖顺序的偏差——我们希望它能泛化到所有名字，而不是过拟合到它们在文件中出现的序列。",
    badge: "洗牌大师"
  },
  2: {
    title: "Value类",
    subtitle: "自动微分与计算图",
    heading: "第2关：Value类",
    body: `训练神经网络需要**梯度**：对于每个参数，我们需要知道"如果我向上微调这个数字，损失会上升还是下降，幅度多大？"

**Value**类从零实现自动微分。将每个操作想象成一个乐高积木，它知道：
1. 如何计算其输出（前向传播）
2. 其输出相对于其输入如何变化（局部梯度）

**示例**：对于乘法\`a * b\`，前向传播给出\`a·b\`，局部梯度为\`∂(a·b)/∂a = b\`和\`∂(a·b)/∂b = a\`。

\`backward()\`方法应用**链式法则**：如果损失为L，节点v有子节点c，局部梯度为∂v/∂c，则：

\`\`\`
∂L/∂c += (∂v/∂c) × (∂L/∂v)
\`\`\`

这只是沿路径乘以变化率！就像："汽车比自行车快2倍，自行车比步行快4倍，所以汽车比步行快2×4=8倍。"

**生产环境差异**：PyTorch做同样的事情，但在张量（数组）上而不是标量上，在GPU上运行以实现大规模并行。`,
    question: "为什么`__mul__`将`(other.data, self.data)`存储为local_grads？",
    answer: "因为d(a×b)/da = b且d(a×b)/db = a。通过将另一个操作数的值存储为局部梯度，backward()可以自动应用链式法则。",
    badge: "链式法则大师"
  },
  3: {
    title: "嵌入",
    subtitle: "wte + wpe矩阵",
    heading: "第3关：嵌入",
    body: `在其他任何事情之前，每个token必须变成一个数字向量。microgpt使用**两个可学习的查找表**：

**wte**（权重token嵌入）：将每个token ID映射到一个16维向量。可以将其视为"这是什么字符？"

**wpe**（权重位置嵌入）：将每个位置（0–15）映射到一个16维向量。可以将其视为"我在序列中的哪个位置？"

这两个向量按元素**相加**，在单个16维向量中为模型提供身份和位置信息。`,
    question: "为什么要相加token和位置嵌入而不是拼接它们？",
    answer: "相加保持维度恒定（16）而不是加倍（32），这在参数上更有效。模型学习在同一空间内解开身份与位置的纠缠。",
    badge: "空间节省者"
  },
  4: {
    title: "RMSNorm",
    subtitle: "保持信号稳定",
    heading: "第4关：RMSNorm",
    body: `如果没有归一化，激活值在网络深处可能呈指数级增长，导致**梯度爆炸或消失**。

**RMSNorm**（均方根归一化）重新缩放向量，使其均方根等于~1。与LayerNorm不同，它跳过了居中（不减去均值），使其更便宜。

**1e-5 epsilon**防止向量全为零时除以零。

microgpt在每个注意力块和MLP之前应用RMSNorm——在整个训练过程中保持信号良好调节。`,
    question: "为什么使用RMSNorm而不是GPT-2的原始LayerNorm？",
    answer: "RMSNorm更简单（不减去均值）并且训练效果相当好。它在LLaMA和Mistral等现代模型中使用，原因相同——效率而不牺牲质量。",
    badge: "稳定专家"
  },
  5: {
    title: "多头注意力",
    subtitle: "Q·K/√d → softmax → V",
    heading: "第5关：多头注意力",
    body: `注意力是**通信机制**——这是位置t的token可以"查看"过去token（位置0..t-1）的唯一地方。

每个token被投影到三个角色，通过三个可学习矩阵：

- **查询（Query, Q）**："我在寻找什么？"
- **键（Key, K）**："我包含什么？"
- **值（Value, V）**："如果被选中，我提供什么信息？"

**缩放点积**：相似度Q·K / √head_dim为每个过去的token给出一个分数。除以√head_dim可以防止高维度中的softmax饱和。

**多头注意力**（这里4个头）让模型同时关注不同的模式——每个头在不同类型的关系上学习。head_dim=4时，所有4个头合并后仍然产生16维输出。

**因果掩码**：未来的token被遮蔽（在softmax之前设为-∞），所以模型只关注过去——这对自回归生成至关重要。

**残差连接** x = x_attn + x_residual 让梯度完全绕过注意力块，防止更深网络中的梯度消失。`,
    question: "为什么在softmax实现中使用max_val减法？",
    answer: "减去最大值可以防止大logits指数化时的溢出（例如，exp(1000) = 无穷大）。这在数学上是等价的，但在数值上是稳定的：softmax(x) = softmax(x - max)。",
    badge: "Softmax大师"
  },
  6: {
    title: "多层感知机",
    subtitle: "FC1 → ReLU → FC2",
    heading: "第6关：多层感知机",
    body: `在注意力（**跨token**混合信息）之后，MLP**独立地**处理每个token——这是模型存储事实知识的地方。

**FC1**从16维扩展到64维（4倍）。这个更宽的空间让模型能够表示复杂的组合。
**ReLU**添加非线性——没有它，整个网络只是一个线性变换。
**FC2**从64维投影回16维。

扩展-收缩模式充当"特征检测器"——64维层中的神经元专门处理特定模式。`,
    question: "为什么GPT-2使用GeLU而microgpt使用ReLU？",
    answer: "GeLU是平滑的（没有硬零截断），这可以帮助非常深的网络的梯度流动。对于微型的microgpt（1层），简单的ReLU同样有效，并且从头实现更简单。",
    badge: "激活函数专家"
  },
  7: {
    title: "Transformer块",
    subtitle: "预归一化 + 残差流",
    heading: "第7关：Transformer块",
    body: `现在我们了解了每个组件，让我们看看它们如何组合成完整的gpt()函数。

架构具有清晰的**两层结构**：
1. 最开始一个嵌入步骤（wte + wpe）
2. 重复的transformer层循环（n_layer次）

每个**transformer层**本身是两个子块，遵循相同的模式：
x_residual = x → RMSNorm → [子块] → x += x_residual

这种**预归一化 + 残差**模式是现代transformer的核心工作机制。残差在网络中创建了一条"高速公路"——梯度可以在反向传播时跳过任何子块。

**残差流**概念：将x视为积累信息的运行总和。每层从中读取（通过归一化输入）并写入（通过残差加法）。嵌入是第一次写入；LM头是最后一次读取。

**扩展到GPT-3**（1750亿参数）只需：更大的n_embd（12288），更多头（96），更深的n_layer（96）——架构完全相同。`,
    question: "为什么RMSNorm在每个子块之前（预归一化）而不是之后（后归一化，如原始GPT-2）？",
    answer: "预归一化使残差流保持在可预测的尺度上——子块总是接收归一化的输入。后归一化在残差加法之后归一化输出，这可能导致残差主导并使早期训练不稳定。预归一化被LLaMA、Mistral和大多数现代transformer采用。",
    badge: "架构师"
  },
  8: {
    title: "损失函数",
    subtitle: "通过.log()的交叉熵",
    heading: "第8关：损失函数",
    body: `模型通过**最大似然**训练：最大化正确的下一个字符的概率。

这等价于**最小化交叉熵**：\`loss = -log(p_correct)\`

当模型自信且正确时，p_correct ≈ 1 → loss ≈ 0。
当模型错误或不确定时，p_correct ≈ 0 → loss → ∞。

**\`.log()\`**方法在Value节点上记录反向传播规则：d(log x)/dx = 1/x。当调用\`.backward()\`时，梯度通过softmax、通过注意力头，一直流回嵌入矩阵。`,
    question: "为什么在softmax中使用指数函数(exp)将logits转换为概率？",
    answer: "exp确保所有输出都是正数（概率必须≥ 0），创建一个平滑的可微函数，并放大logits之间的差异——随着训练的进行，使模型更具决断力。",
    badge: "对数似然"
  },
  9: {
    title: "Adam优化器",
    subtitle: "动量和速度缓冲区",
    heading: "第9关：Adam优化器",
    body: `普通的梯度下降(SGD)可能很慢且不稳定。**Adam**为每个参数添加两个动量缓冲区：

**m**（一阶矩）：梯度的指数移动平均——平滑更新方向。
**v**（二阶矩）：平方梯度的指数移动平均——跟踪梯度有多大。

更新\`m / sqrt(v)\`是一个**每个参数的自适应学习率**：历史上梯度大的参数获得较小的更新（已经学习），而很少见的参数获得较大的更新。

**偏差校正**（除以1-β^t）在早期步骤中至关重要，因为此时m和v偏向零。`,
    question: "为什么microgpt使用beta1=0.85而不是标准的0.9？",
    answer: "较低的beta1使优化器不那么'粘滞'——它更快地忘记旧梯度。对于在短序列上训练的小模型，这允许更快的适应，并防止动量在这个小数据集上过冲。",
    badge: "Adam耳语者"
  },
  10: {
    title: "推理",
    subtitle: "温度采样",
    heading: "第10关：推理",
    body: `训练后，我们可以**采样新名字**自回归地——一次一个字符。

**温度**控制创造性：
- **低T (0.1)**：非常自信，选择可能的字符 → 重复的名字
- **T = 1.0**：直接从学习的概率中采样
- **高T (2.0)**：平坦化分布 → 更随机、更有创意的名字

在每一步，模型获取当前token，运行整个transformer，获得所有字符的分布，采样一个，并将其作为下一个输入反馈。这一过程持续到模型输出BOS（表示"完成"）。`,
    question: "为什么用BOS token而不是随机字符开始推理？",
    answer: "BOS是一个学习到的'名字开始'信号。模型在每个训练样本的开头都看到了BOS，因此它学会了将BOS与名字开始字符的分布（通常是元音和常见的首字母）联系起来。",
    badge: "推理大师"
  }
};
